import { useConfigContext } from '@/context/configContext';
import { RoiInfoTooltip } from '@/dashboards/SpendSummary/components/RoiInfoTooltip';
import { TotalRoiScopeTooltip } from '@/dashboards/SpendSummary/components/TotalRoiScopeTooltip';
import {
    useRoiConfig,
    useSpendSummaryContext,
    useTotalMediaRoi,
} from '@/dashboards/SpendSummary/hooks';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { ReactNode } from 'react';
import { Typography } from '@analytical-alley/ui';

const TotalTile = ({
    title,
    value,
    tooltip,
    titleTooltip,
}: {
    title: ReactNode;
    value: ReactNode;
    tooltip?: ReactNode;
    titleTooltip?: string;
}) => {
    const label = (
        <Typography
            tooltip={titleTooltip}
            className="flex flex-row flex-wrap justify-center font-medium"
        >
            <span>Total&nbsp;</span>
            <span>{title}</span>
        </Typography>
    );

    return (
        <div className="glass-tile flex flex-col flex-1 justify-start items-center gap-4 px-6 py-4">
            {tooltip ? (
                <span className="flex gap-1 font-medium">
                    {label}
                    {tooltip}
                </span>
            ) : (
                label
            )}
            <span className="text-[32px] font-normal leading-none">
                {value}
            </span>
        </div>
    );
};

/** The three headline KPI cards above the Spend Summary table. */
export const SpendSummaryTotals = () => {
    const { formatNumber } = useConfigContext();
    const { contributionFormatter } = useDashboardGlobalContext();
    const { spendSummaryData } = useSpendSummaryContext();
    const { name } = useRoiConfig();
    const {
        totalRoi,
        isModeledVariablesOnly,
        includedVariables,
        spendWithoutContribution,
    } = useTotalMediaRoi();

    return (
        <div className="font-mono flex flex-row justify-stretch max-lg:flex-wrap gap-4 mb-6">
            <TotalTile
                title="Media Contribution"
                titleTooltip="KPI generated by media in a selected period."
                value={contributionFormatter(
                    spendSummaryData.totalContribution,
                )}
            />
            <TotalTile
                title="Media Spend"
                titleTooltip="Money spent on media over a selected period."
                value={formatNumber(spendSummaryData.totalSpend)}
            />
            <TotalTile
                title={`Media ${name}`}
                value={
                    // Only worth explaining when the KPI covers fewer variables
                    // than the table below it.
                    isModeledVariablesOnly ? (
                        <TotalRoiScopeTooltip
                            roiName={name}
                            includedVariables={includedVariables}
                            spendWithoutContribution={spendWithoutContribution}
                        >
                            {totalRoi}
                        </TotalRoiScopeTooltip>
                    ) : (
                        totalRoi
                    )
                }
                tooltip={<RoiInfoTooltip />}
            />
        </div>
    );
};
